import React, { useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getAuthToken } from "../../redux/reducers/auth";
import { getUserId } from "../../redux/reducers/user";

import { getChatSocket } from "../../services/socket/useSocket";
import SocketServerEvents from "../../services/socket/SocketServerEvents";
import ISonicMessage from "../../common/classes/SonicMessage";
import {
  addNotification,
  addRoom,
  clearChatState,
  getRooms,
  getStateChatRooms,
  setLatestMessage,
} from "../../redux/reducers/chat";

const ChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const userId = useAppSelector(getUserId);
  const authToken = useAppSelector(getAuthToken);
  const chatRooms = useAppSelector(getStateChatRooms);

  const dispatch = useAppDispatch();

  const genNotice = (message: ISonicMessage) => {
    switch (message.requestType) {
      case "offer":
        return new Notification(
          message.relationOwner === userId
            ? "オファーがありました。"
            : "オファーしました。"
        );
      case "project":
        return new Notification(
          message.relationOwner === userId
            ? "プロジェクトに応募がありました。"
            : "プロジェクトに応募しました。"
        );
      default:
        return new Notification(
          `${message.senderName}: ${message.message}${
            message.img ? " [画像]" : ""
          }`
        );
    }
  };

  const ioExecuted = useRef(false);

  useEffect(() => {
    if (ioExecuted.current) return;

    if (authToken) {
      // ログインしてからSocket通信を開始します。
      const socket = getChatSocket();

      // ここでログイン状態やアカウント情報に依存しないイベントを登録します。
      if (process.env.NODE_ENV !== "production") {
        socket.on("connect", () => {
          console.log("connected");
        });

        socket.on("disconnect", (reason) => {
          console.log(`disconnect reason: ${reason}`);
        });

        socket.on("error", (error) => {
          console.log(`error: ${error}`);
        });

        socket.on("connect_error", (error) => {
          console.log(`connect_error: ${error}`);
        });

        socket.on("reconnect_error", (error) => {
          console.log(`reconnect_error: ${error}`);
        });

        socket.on(SocketServerEvents.myError, (message: string) => {
          console.log(message);
        });
      }

      socket.on(SocketServerEvents.roomCreated, (room) => {
        dispatch(addRoom(room));
      });

      ioExecuted.current = true;
    }
  }, [authToken]);

  // ここにログイン状態やアカウント情報に依存するイベントを登録します。
  useEffect(() => {
    if (!ioExecuted.current) return;

    const socket = getChatSocket();

    // ログアウト・再ログインでイベントを再登録します。
    if (userId) {
      socket.on(
        SocketServerEvents.receivedMessage,
        (message: ISonicMessage) => {
          if (message.senderId === userId)
            return dispatch(setLatestMessage(message));
          if (
            "Notification" in window &&
            Notification.permission !== "denied"
          ) {
            if (Notification.permission === "granted") {
              genNotice(message);
            } else {
              Notification.requestPermission();
            }
          }

          dispatch(addNotification(message));
        }
      );

      socket.on(SocketServerEvents.refresh, () => {
        if (authToken) dispatch(getRooms(authToken));
      });
    } else {
      socket.off(SocketServerEvents.receivedMessage);
      socket.off(SocketServerEvents.refresh);
    }
  }, [userId, ioExecuted.current]);

  // チャットルーム一覧の処理
  useEffect(() => {
    if (authToken) {
      dispatch(getRooms(authToken));
    } else {
      if (chatRooms.length > 0) {
        dispatch(clearChatState());
      }
    }
  }, [authToken]);

  return <>{children}</>;
};

export default ChatProvider;
